import { supabase } from '../lib/supabase';


/**
 * Submits a portal access request from the public form.
 * - Inserts directly into portal_access_requests with status='pendiente'.
 * - No client lookup, no auth, no notifications, no secondary awaits.
 */
export async function submitPortalAccessRequest({ nombre, telefono, email }) {
  try {
    const trimmedNombre = (nombre || '')?.trim();
    const trimmedTelefono = (telefono || '')?.trim();
    const trimmedEmail = (email || '')?.trim()?.toLowerCase();

    if (!trimmedNombre) {
      return { success: false, error: 'El nombre completo es requerido.' };
    }

    const payload = {
      nombre: trimmedNombre,
      telefono: trimmedTelefono || null,
      email: trimmedEmail || null,
      status: 'pendiente',
      created_at: new Date()?.toISOString(),
    };

    console.log('[portalAccessRequestService] inserting payload:', payload);

    const { data: inserted, error: insertError } = await supabase?.from('portal_access_requests')?.insert(payload)?.select('id')?.single();

    if (insertError) {
      console.error('[portalAccessRequestService] INSERT FAILED:', {
        code: insertError?.code,
        message: insertError?.message,
        details: insertError?.details,
        hint: insertError?.hint,
        payload,
      });
      return { success: false, error: `Error al guardar: ${insertError?.message}` };
    }

    console.log('[portalAccessRequestService] insert success, id:', inserted?.id);
    return { success: true, existingClient: false, requestId: inserted?.id };
  } catch (err) {
    console.error('[portalAccessRequestService] unexpected error:', err);
    return { success: false, error: `Error inesperado: ${err?.message || 'Intenta de nuevo.'}` };
  }
}

/**
 * Fetch all portal access requests for a company, ordered by created_at desc.
 */
export async function fetchPortalAccessRequests(companyId) {
  try {
    const { data, error } = await supabase?.from('portal_access_requests')?.select(`
        *,
        matched_client:clients!portal_access_requests_matched_cliente_id_fkey(
          id, nombre, email, telefono, company_id, portal_user_id
        )
      `)?.or(`company_id.eq.${companyId},company_id.is.null`)?.order('created_at', { ascending: false });

    if (error) {
      console.error('[portalAccessRequestService] fetch error:', error);
      return { success: false, error: 'No se pudieron cargar las solicitudes.', data: [] };
    }
    return { success: true, data: data || [] };
  } catch (err) {
    console.error('[portalAccessRequestService] fetch unexpected error:', err);
    return { success: false, error: 'Error inesperado al cargar.', data: [] };
  }
}

/**
 * Count pending portal access requests for a company (for badge).
 */
export async function countPendingPortalAccessRequests(companyId) {
  try {
    const { count, error } = await supabase?.from('portal_access_requests')?.select('id', { count: 'exact', head: true })?.eq('status', 'pendiente')?.or(`company_id.eq.${companyId},company_id.is.null`);

    if (error) {
      console.error('[portalAccessRequestService] count error:', error);
      return 0;
    }
    return count || 0;
  } catch (err) {
    console.error('[portalAccessRequestService] count unexpected error:', err);
    return 0;
  }
}

/**
 * Approve a portal access request (status → 'aprobado').
 * Also auto-links matched client if phone/email match found.
 */
export async function approvePortalAccessRequest(requestId, reviewedBy) {
  try {
    // Fetch the request first to check for auto-match
    const { data: req, error: fetchErr } = await supabase?.from('portal_access_requests')?.select('id, nombre, telefono, email, matched_cliente_id, company_id')?.eq('id', requestId)?.maybeSingle();

    if (fetchErr) {
      console.error('[portalAccessRequestService] approve fetch error:', fetchErr);
      return { success: false, error: `Error al obtener solicitud: ${fetchErr?.message}` };
    }

    let matchedClienteId = req?.matched_cliente_id;

    // Auto-match by phone or email if not already linked
    if (!matchedClienteId && req?.company_id) {
      try {
        if (req?.telefono) {
          const { data: byPhone } = await supabase?.from('clients')?.select('id')?.eq('telefono', req?.telefono)?.eq('company_id', req?.company_id)?.maybeSingle();
          if (byPhone?.id) matchedClienteId = byPhone?.id;
        }
        if (!matchedClienteId && req?.email) {
          const { data: byEmail } = await supabase?.from('clients')?.select('id')?.ilike('email', req?.email)?.eq('company_id', req?.company_id)?.maybeSingle();
          if (byEmail?.id) matchedClienteId = byEmail?.id;
        }
      } catch (matchErr) {
        console.warn('[portalAccessRequestService] auto-match skipped:', matchErr?.message);
      }
    }

    const updatePayload = {
      status: 'aprobado',
      reviewed_at: new Date()?.toISOString(),
      reviewed_by: reviewedBy || null,
    };
    if (matchedClienteId) updatePayload.matched_cliente_id = matchedClienteId;

    const { error } = await supabase?.from('portal_access_requests')?.update(updatePayload)?.eq('id', requestId);

    if (error) {
      console.error('[portalAccessRequestService] approve error:', error);
      return { success: false, error: `No se pudo aprobar: ${error?.message}` };
    }
    return { success: true };
  } catch (err) {
    console.error('[portalAccessRequestService] approve unexpected error:', err);
    return { success: false, error: 'Error inesperado al aprobar.' };
  }
}

/**
 * Reject a portal access request (status → 'rechazado').
 */
export async function rejectPortalAccessRequest(requestId, reviewedBy) {
  try {
    const { error } = await supabase?.from('portal_access_requests')?.update({
        status: 'rechazado',
        reviewed_at: new Date()?.toISOString(),
        reviewed_by: reviewedBy || null,
      })?.eq('id', requestId);

    if (error) {
      console.error('[portalAccessRequestService] reject error:', error);
      return { success: false, error: `No se pudo rechazar: ${error?.message}` };
    }
    return { success: true };
  } catch (err) {
    console.error('[portalAccessRequestService] reject unexpected error:', err);
    return { success: false, error: 'Error inesperado al rechazar.' };
  }
}

/**
 * Link an existing client to a portal access request.
 */
export async function linkClienteToRequest(requestId, clienteId, companyId) {
  try {
    const { error } = await supabase?.from('portal_access_requests')?.update({
        matched_cliente_id: clienteId,
        company_id: companyId || null,
      })?.eq('id', requestId);

    if (error) {
      console.error('[portalAccessRequestService] link client error:', error);
      return { success: false, error: `No se pudo vincular: ${error?.message}` };
    }
    return { success: true };
  } catch (err) {
    console.error('[portalAccessRequestService] link client unexpected error:', err);
    return { success: false, error: 'Error inesperado al vincular.' };
  }
}

/**
 * Create a new client from a portal access request and link it.
 */
export async function createClientFromRequest(requestId, { nombre, telefono, email, company_id }) {
  try {
    if (!nombre?.trim()) {
      return { success: false, error: 'El nombre es requerido para crear el cliente.' };
    }

    // Check for duplicate by phone or email
    if (telefono || email) {
      const orParts = [];
      if (telefono) orParts?.push(`telefono.eq.${telefono}`);
      if (email) orParts?.push(`email.ilike.${email}`);
      const { data: existing } = await supabase?.from('clients')?.select('id, nombre')?.eq('company_id', company_id)?.or(orParts?.join(','))?.maybeSingle();

      if (existing?.id) {
        // Already exists — just link
        const linkResult = await linkClienteToRequest(requestId, existing?.id, company_id);
        if (linkResult?.success) {
          return { success: true, clientId: existing?.id, linked: true, message: `Cliente existente "${existing?.nombre}" vinculado.` };
        }
        return linkResult;
      }
    }

    // Create new client
    const { data: newClient, error: createErr } = await supabase?.from('clients')?.insert({
        nombre: nombre?.trim(),
        telefono: telefono || null,
        email: email || null,
        company_id,
      })?.select('id')?.single();

    if (createErr) {
      console.error('[portalAccessRequestService] create client error:', createErr);
      return { success: false, error: `Error al crear cliente: ${createErr?.message}` };
    }

    // Link to request
    const linkResult = await linkClienteToRequest(requestId, newClient?.id, company_id);
    if (!linkResult?.success) return linkResult;

    return { success: true, clientId: newClient?.id, created: true };
  } catch (err) {
    console.error('[portalAccessRequestService] createClientFromRequest unexpected error:', err);
    return { success: false, error: `Error inesperado: ${err?.message}` };
  }
}

/**
 * Helper: fetch the manage-portal-user Edge Function with timeout.
 * Returns { success, ...data } or throws on network error.
 */
async function callManagePortalUser(payload) {
  const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env?.VITE_SUPABASE_ANON_KEY;

  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Configuración de Supabase no disponible.');
  }

  const url = `${supabaseUrl}/functions/v1/manage-portal-user`;

  // Try up to 2 times with a 12-second timeout each
  let lastErr;
  for (let attempt = 1; attempt <= 2; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller?.abort(), 12000);

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
        },
        body: JSON.stringify(payload),
        signal: controller?.signal,
      });

      clearTimeout(timeoutId);

      if (!response?.ok) {
        const text = await response?.text()?.catch(() => '');
        throw new Error(`HTTP ${response.status}: ${text || response.statusText}`);
      }

      const data = await response?.json();
      return data;
    } catch (err) {
      clearTimeout(timeoutId);
      lastErr = err;
      console.warn(`[portalAccessRequestService] manage-portal-user attempt ${attempt} failed:`, err?.message);

      if (attempt < 2) {
        // Wait 1 second before retry
        await new Promise((r) => setTimeout(r, 1000));
      }
    }
  }

  // Both attempts failed
  const isAbort = lastErr?.name === 'AbortError';
  const isNetwork = lastErr?.message === 'Load failed' || lastErr?.message === 'Failed to fetch' || lastErr?.name === 'TypeError';

  if (isAbort) {
    throw new Error('La solicitud tardó demasiado. Verifica tu conexión e intenta de nuevo.');
  }
  if (isNetwork) {
    throw new Error('No se pudo conectar con el servicio de autenticación. Verifica que la función Edge esté desplegada en Supabase y que la URL sea correcta.');
  }
  throw lastErr;
}

/**
 * Create portal access for a client:
 * 1. Calls manage-portal-user Edge Function (admin API) to create or update Auth user
 *    with the generated temp password — so the password is REAL in Auth.
 * 2. Updates the client's portal_user_id * 3. Marks request as'activacion_enviada'
 * 4. Saves temp_password to DB only after Auth confirms success
 */
export async function createPortalAccess(requestId, clienteId, email, reviewedBy) {
  try {
    if (!email) {
      return { success: false, error: 'Se requiere correo electrónico para crear acceso.' };
    }

    const normalizedEmail = email?.trim()?.toLowerCase();

    const { data: clientData, error: clientFetchErr } = await supabase?.from('clients')?.select('id, portal_user_id, company_id, nombre, telefono')?.eq('id', clienteId)?.maybeSingle();

    if (clientFetchErr) {
      console.error('[portalAccessRequestService] client fetch error:', clientFetchErr);
      return { success: false, error: `Error al verificar cliente: ${clientFetchErr?.message}` };
    }

    const companyId = clientData?.company_id;
    if (!companyId) {
      console.error('[portalAccessRequestService] client has no company_id:', clienteId);
      return { success: false, error: 'El cliente no tiene empresa asignada. Vincula el cliente a la empresa antes de crear acceso.' };
    }

    // Generate temp password
    const tempPassword = `GC_${Math.random()?.toString(36)?.slice(2, 10)?.toUpperCase()}!`;

    // Call Edge Function (admin API) to create or update Auth user with the real password
    let authResult;
    try {
      authResult = await callManagePortalUser({
        action: 'create_or_update',
        email: normalizedEmail,
        password: tempPassword,
        nombre: clientData?.nombre || '',
      });
    } catch (fetchErr) {
      console.error('[portalAccessRequestService] manage-portal-user fetch error:', fetchErr);
      return { success: false, error: `Error al conectar con el servicio de autenticación: ${fetchErr?.message}` };
    }

    if (!authResult?.success) {
      console.error('[portalAccessRequestService] manage-portal-user error:', authResult);
      return { success: false, error: `Error al crear/actualizar acceso en Auth: ${authResult?.error || 'Error desconocido'}` };
    }

    const portalUserId = authResult?.userId;
    console.log('[portalAccessRequestService] Auth user ready, id:', portalUserId, 'action:', authResult?.action);

    // Auth confirmed — now save temp_password and mark access created
    const { error: accessUpdateErr } = await supabase?.from('portal_access_requests')?.update({
      temp_password: tempPassword,
      password_changed_by_client: false,
      temp_password_set_at: new Date()?.toISOString(),
      acceso_creado: true,
      requiere_cambio_password: true,
      password_temporal_activa: true,
      acceso_activado: false,
    })?.eq('id', requestId);

    if (accessUpdateErr) {
      console.error('[portalAccessRequestService] access update error:', accessUpdateErr);
      // Non-fatal — Auth was set, just log
    }

    // Link portal_user_id to client
    if (portalUserId && clienteId) {
      const { error: clientUpdateErr } = await supabase?.from('clients')?.update({ portal_user_id: portalUserId })?.eq('id', clienteId);
      if (clientUpdateErr) {
        console.error('[portalAccessRequestService] client portal_user_id update error:', clientUpdateErr);
      } else {
        console.log('[portalAccessRequestService] client.portal_user_id set to', portalUserId);
      }

      const { error: profileUpdateErr } = await supabase?.from('user_profiles')?.update({ company_id: companyId })?.eq('id', portalUserId)?.is('company_id', null);
      if (profileUpdateErr) {
        console.warn('[portalAccessRequestService] user_profiles company_id patch skipped:', profileUpdateErr?.message);
      } else {
        console.log('[portalAccessRequestService] user_profiles.company_id patched for portal user', portalUserId);
      }
    }

    // WhatsApp notification (non-critical)
    const activationUrl = `${window.location?.origin}/portal`;
    const telefono = clientData?.telefono;
    if (telefono) {
      const whatsappMsg = `Hola ${clientData?.nombre || ''}, tu acceso al Portal Cliente ha sido creado. Ingresa aquí: ${activationUrl}`;
      console.info('[portalAccessRequestService] WhatsApp activation (prepared):', { telefono, message: whatsappMsg });
      const n8nUrl = import.meta.env?.VITE_N8N_WEBHOOK_URL;
      if (n8nUrl && n8nUrl !== 'your-n8n-webhook-url-here') {
        try {
          await fetch(n8nUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'portal_activation',
              telefono,
              nombre: clientData?.nombre,
              activationUrl,
              message: whatsappMsg,
            }),
          });
        } catch (whatsappErr) {
          console.warn('[portalAccessRequestService] WhatsApp send failed (non-critical):', whatsappErr?.message);
        }
      }
    }

    const { error: requestUpdateErr } = await supabase?.from('portal_access_requests')?.update({
      status: 'activacion_enviada',
      reviewed_at: new Date()?.toISOString(),
      reviewed_by: reviewedBy || null,
      matched_cliente_id: clienteId || null,
      company_id: companyId,
    })?.eq('id', requestId);

    if (requestUpdateErr) {
      console.error('[portalAccessRequestService] request update error:', requestUpdateErr);
      return { success: false, error: `Acceso creado pero no se pudo actualizar la solicitud: ${requestUpdateErr?.message}` };
    }

    return { success: true, portalUserId, tempPassword, whatsappPrepared: !!telefono };
  } catch (err) {
    console.error('[portalAccessRequestService] createPortalAccess unexpected error:', err);
    return { success: false, error: `Error inesperado: ${err?.message || 'Intenta de nuevo.'}` };
  }
}

/**
 * Resend activation for a portal access request.
 * Sends via email (resetPasswordForEmail) and/or WhatsApp if phone available.
 * @param {string} requestId
 * @param {string|null} email
 * @param {string|null} telefono
 */
export async function resendActivation(requestId, email, telefono) {
  try {
    if (!email && !telefono) {
      return { success: false, error: 'Se requiere email o teléfono para reenviar activación.' };
    }

    const activationUrl = `${window.location?.origin}/portal`;
    let emailSent = false;
    let whatsappPrepared = false;

    // Send email activation link
    if (email) {
      const { error: resetErr } = await supabase?.auth?.resetPasswordForEmail(
        email?.trim()?.toLowerCase(),
        { redirectTo: activationUrl }
      );
      if (resetErr) {
        console.error('[portalAccessRequestService] resend email error:', resetErr);
        // Non-fatal — continue with WhatsApp
      } else {
        emailSent = true;
      }
    }

    // Send WhatsApp
    if (telefono) {
      const whatsappMsg = `Hola, te reenviamos el enlace de acceso al Portal Cliente: ${activationUrl}`;
      console.info('[portalAccessRequestService] WhatsApp resend (prepared):', { telefono, message: whatsappMsg });
      const n8nUrl = import.meta.env?.VITE_N8N_WEBHOOK_URL;
      if (n8nUrl && n8nUrl !== 'your-n8n-webhook-url-here') {
        try {
          await fetch(n8nUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'portal_resend',
              telefono,
              activationUrl,
              message: whatsappMsg,
            }),
          });
          whatsappPrepared = true;
        } catch (whatsappErr) {
          console.warn('[portalAccessRequestService] WhatsApp resend failed (non-critical):', whatsappErr?.message);
        }
      } else {
        whatsappPrepared = true; // prepared but no integration yet
      }
    }

    if (!emailSent && !whatsappPrepared) {
      return { success: false, error: 'No se pudo reenviar la activación por ningún medio.' };
    }

    // Update sent_at timestamp on the request
    await supabase?.from('portal_access_requests')?.update({ updated_at: new Date()?.toISOString() })?.eq('id', requestId);

    return { success: true, emailSent, whatsappPrepared };
  } catch (err) {
    console.error('[portalAccessRequestService] resendActivation unexpected error:', err);
    return { success: false, error: 'Error inesperado al reenviar.' };
  }
}

/**
 * @deprecated Use resendActivation instead
 */
export async function resendActivationEmail(requestId, email) {
  return resendActivation(requestId, email, null);
}

/**
 * Generate a new temporary password for an existing portal access request.
 * Calls manage-portal-user Edge Function (admin API) to update the REAL Auth password.
 * Only saves temp_password to DB after Auth confirms the update succeeded.
 * The previous temp password is immediately invalidated.
 */
export async function resetTempPassword(requestId) {
  try {
    if (!requestId) {
      return { success: false, error: 'ID de solicitud requerido.' };
    }

    const { data: req, error: fetchErr } = await supabase?.from('portal_access_requests')?.select('id, email, matched_cliente_id')?.eq('id', requestId)?.maybeSingle();

    if (fetchErr || !req) {
      return { success: false, error: 'No se encontró la solicitud.' };
    }

    if (!req?.email) {
      return { success: false, error: 'La solicitud no tiene correo electrónico asociado.' };
    }

    const newTempPassword = `GC_${Math.random()?.toString(36)?.slice(2, 10)?.toUpperCase()}!`;

    // Call Edge Function (admin API) to update the real Auth password
    let authResult;
    try {
      authResult = await callManagePortalUser({
        action: 'create_or_update',
        email: req?.email?.trim()?.toLowerCase(),
        password: newTempPassword,
      });
    } catch (fetchErr) {
      console.error('[portalAccessRequestService] manage-portal-user fetch error:', fetchErr);
      return { success: false, error: `Error al conectar con el servicio de autenticación: ${fetchErr?.message}` };
    }

    if (!authResult?.success) {
      console.error('[portalAccessRequestService] manage-portal-user reset error:', authResult);
      return { success: false, error: `Error al restablecer contraseña en Auth: ${authResult?.error || 'Error desconocido'}` };
    }

    console.log('[portalAccessRequestService] Auth password updated for reset, userId:', authResult?.userId);

    // Auth confirmed — now save the new temp_password and invalidate the old one
    const { error: updateErr } = await supabase?.from('portal_access_requests')?.update({
      temp_password: newTempPassword,
      password_changed_by_client: false,
      temp_password_set_at: new Date()?.toISOString(),
      acceso_creado: true,
      requiere_cambio_password: true,
      password_temporal_activa: true,
      acceso_activado: false,
    })?.eq('id', requestId);

    if (updateErr) {
      console.error('[portalAccessRequestService] resetTempPassword update error:', updateErr);
      return { success: false, error: `Contraseña actualizada en Auth pero error al guardar en BD: ${updateErr?.message}` };
    }

    return { success: true, tempPassword: newTempPassword };
  } catch (err) {
    console.error('[portalAccessRequestService] resetTempPassword unexpected error:', err);
    return { success: false, error: `Error inesperado: ${err?.message}` };
  }
}

/**
 * Set a definitive (non-temporary) password for a portal access request.
 * Calls manage-portal-user Edge Function to update the real Auth password.
 * Marks the access as activated — client can use it immediately without being forced to change it.
 */
export async function setDefinitivePassword(requestId, password) {
  try {
    if (!requestId) {
      return { success: false, error: 'ID de solicitud requerido.' };
    }
    if (!password || password?.length < 6) {
      return { success: false, error: 'La contraseña debe tener al menos 6 caracteres.' };
    }

    const { data: req, error: fetchErr } = await supabase?.from('portal_access_requests')?.select('id, email, matched_cliente_id')?.eq('id', requestId)?.maybeSingle();

    if (fetchErr || !req) {
      return { success: false, error: 'No se encontró la solicitud.' };
    }

    if (!req?.email) {
      return { success: false, error: 'La solicitud no tiene correo electrónico asociado.' };
    }

    // Call Edge Function (admin API) to set the real Auth password
    let authResult;
    try {
      authResult = await callManagePortalUser({
        action: 'create_or_update',
        email: req?.email?.trim()?.toLowerCase(),
        password,
      });
    } catch (fetchErr) {
      console.error('[portalAccessRequestService] manage-portal-user fetch error (setDefinitivePassword):', fetchErr);
      return { success: false, error: `Error al conectar con el servicio de autenticación: ${fetchErr?.message}` };
    }

    if (!authResult?.success) {
      console.error('[portalAccessRequestService] manage-portal-user error (setDefinitivePassword):', authResult);
      return { success: false, error: `Error al establecer contraseña en Auth: ${authResult?.error || 'Error desconocido'}` };
    }

    // Mark access as definitively activated — no forced password change
    const { error: updateErr } = await supabase?.from('portal_access_requests')?.update({
      temp_password: null,
      password_changed_by_client: true,
      temp_password_set_at: null,
      acceso_creado: true,
      requiere_cambio_password: false,
      password_temporal_activa: false,
      acceso_activado: true,
      status: 'activado',
    })?.eq('id', requestId);

    if (updateErr) {
      console.error('[portalAccessRequestService] setDefinitivePassword update error:', updateErr);
      return { success: false, error: `Contraseña establecida en Auth pero error al guardar en BD: ${updateErr?.message}` };
    }

    // Also link portal_user_id to client if not already set
    if (authResult?.userId && req?.matched_cliente_id) {
      await supabase?.from('clients')?.update({ portal_user_id: authResult?.userId })?.eq('id', req?.matched_cliente_id)?.is('portal_user_id', null);
    }

    return { success: true, userId: authResult?.userId };
  } catch (err) {
    console.error('[portalAccessRequestService] setDefinitivePassword unexpected error:', err);
    return { success: false, error: `Error inesperado: ${err?.message}` };
  }
}
